import { ReactNode } from 'react'
import { Tabs, Text, cn, type TabsItem } from '@cloudflare/kumo'

export const KUMO_PAGE_HEADER_VARIANTS = {
  spacing: {
    compact: {
      classes: 'gap-1',
      description: 'Compact spacing between header elements',
    },
    base: {
      classes: 'gap-2',
      description: 'Default spacing between header elements',
    },
    relaxed: {
      classes: 'gap-4',
      description: 'Relaxed spacing for more prominent headers',
    },
  },
} as const

export const KUMO_PAGE_HEADER_DEFAULT_VARIANTS = {
  spacing: 'base',
} as const

export type KumoPageHeaderSpacing = keyof typeof KUMO_PAGE_HEADER_VARIANTS.spacing

export interface KumoPageHeaderVariantsProps {
  spacing?: KumoPageHeaderSpacing
}

export function pageHeaderVariants({
  spacing = KUMO_PAGE_HEADER_DEFAULT_VARIANTS.spacing,
}: KumoPageHeaderVariantsProps = {}) {
  return cn('flex flex-col', KUMO_PAGE_HEADER_VARIANTS.spacing[spacing].classes)
}

export interface PageHeaderProps extends KumoPageHeaderVariantsProps {
  breadcrumbs: ReactNode
  title?: string
  description?: string
  tabs?: TabsItem[]
  defaultTab?: string
  onValueChange?: (value: string) => void
  className?: string
  children?: React.ReactNode
}

export function PageHeader({
  breadcrumbs,
  title,
  description,
  tabs,
  defaultTab,
  onValueChange,
  spacing = 'base',
  className,
  children,
}: PageHeaderProps) {
  return (
    <div className={cn(pageHeaderVariants({ spacing }), className)}>
      <div className="border-b border-kumo-line">{breadcrumbs}</div>

      {(title || description) && (
        <div className="flex flex-col gap-2 py-3 pl-3">
          {title && (
            <Text variant="heading1" as="h1" DANGEROUS_className="font-heading text-kumo-default">
              {title}
            </Text>
          )}
          {description && (
            <Text as="p" variant="secondary" DANGEROUS_className="max-w-prose">
              {description}
            </Text>
          )}
        </div>
      )}

      {tabs && (
        <div className="flex w-full items-center justify-between border-b border-kumo-line pt-1 pb-3 pl-3">
          <Tabs
            tabs={tabs}
            selectedValue={defaultTab}
            onValueChange={(nextValue) => {
              const stringValue = String(nextValue)
              onValueChange?.(stringValue)
            }}
          />

          {children && <div className="flex items-center gap-2">{children}</div>}
        </div>
      )}

      {!tabs && children && (
        <div className="flex items-center justify-end pl-3">
          <div className="flex items-center gap-2">{children}</div>
        </div>
      )}
    </div>
  )
}
